/**
 * Clasificación de los errores de envío de la cola de sincronización.
 *
 * Lógica pura —sin Dexie ni DOM— para poder probarla con `node --test`.
 */
import { ApiClientError, NetworkError } from '@fitness/api-client';

/**
 * La cuenta dueña de la cola ha cerrado sesión (o ha entrado otra) mientras se
 * enviaba. No dice nada de la operación: se queda en la base de su cuenta, sin
 * gastar intentos, y sale cuando esa cuenta vuelva a entrar.
 */
export class AccountInactiveError extends Error {
  constructor() {
    super('La cuenta ya no tiene la sesión abierta en este dispositivo.');
    this.name = 'AccountInactiveError';
  }
}

/**
 * 4xx significa que el servidor ya ha dicho que no: reintentar no cambia nada.
 * Se exceptúan 401 (el cliente refresca el token y vuelve a intentarlo), 408 y
 * 429, que sí son transitorios.
 */
export function isPermanentError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false;
  if (error.status === 401 || error.status === 408 || error.status === 429) return false;
  return error.status >= 400 && error.status < 500;
}

/** Estados con los que el servidor (o el proxy de delante) dice «ahora no». */
const TRANSIENT_STATUSES = new Set([408, 429, 502, 503, 504]);

/**
 * Errores de transporte: la petición no llegó (sin red, sin cobertura, DNS,
 * el móvil cortó la conexión al bloquearse) o el servidor no estaba
 * (reinicio, despliegue). No dicen nada malo de lo que se envía, así que la
 * cola los reintenta **sin límite** en vez de acabar descartando las series
 * de una sesión entera de gimnasio sin cobertura.
 */
export function isTransientError(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  if (error instanceof AccountInactiveError) return true;
  // El cliente ya envuelve en `NetworkError` el `TypeError` de `fetch`
  // («Failed to fetch», «Load failed» en Safari). Cualquier otro error es de
  // código y sí cuenta para el descarte: reintentarlo para siempre lo escondería.
  return error instanceof ApiClientError && TRANSIENT_STATUSES.has(error.status);
}
